import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import NewButton from '@v7/form/src/components/NewButton';
import { URL_MAX_LENGTH, URL_VALIDATION_ERROR } from '@v7/form/src/utils/constant';
import { normalizeUrl } from '@v7/form/src/utils/helpers';

import { ConfigurationModalValidationError } from '@v7/form/src/utils/types';
import { messages } from './messages';

type ConfigurationModalProps = {
  onClose: () => void;
};

const ConfigurationModal = ({ onClose }: ConfigurationModalProps): JSX.Element => {
  const intl = useIntl();
  const [apiUrl, setApiUrl] = useState('');

  const validationError = useMemo((): ConfigurationModalValidationError | null => {
    const value = apiUrl.trim();

    if (!value) return null;

    if (value.length > URL_MAX_LENGTH) return URL_VALIDATION_ERROR.MAX_LENGTH;
    const isValidUrl = normalizeUrl(value);

    if (isValidUrl === URL_VALIDATION_ERROR.INVALID_URL) return URL_VALIDATION_ERROR.INVALID_URL;

    return null;
  }, [apiUrl]);

  const errorMessageMap = {
    [URL_VALIDATION_ERROR.INVALID_URL]: intl.formatMessage(messages.invalidUrl),
    [URL_VALIDATION_ERROR.MAX_LENGTH]: intl.formatMessage(messages.maxLength),
  };

  const errorMessage = validationError ? errorMessageMap[validationError] : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="configuration-modal">
      <div className="w-full max-w-[32rem]  rounded-lg bg-white p-6 shadow-lg">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            <FormattedMessage {...messages.title} />
          </h2>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-900" htmlFor="validate-api-url">
              <FormattedMessage {...messages.validateLabel} />
            </label>
            <input
              id="validate-api-url"
              name="validateApiUrl"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              placeholder={intl.formatMessage(messages.validatePlaceholder)}
              type="text"
              value={apiUrl}
              onChange={(event): void => setApiUrl(event.target.value)}
              aria-invalid={validationError !== null}
              aria-describedby={validationError !== null ? 'api-url-error' : undefined}
            />
            {errorMessage !== null && (
              <p className="mt-1 text-sm text-red-600" id="api-url-error" role="alert">
                {errorMessage}
              </p>
            )}
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <NewButton type="tertiary" onClick={onClose}>
            <FormattedMessage {...messages.cancel} />
          </NewButton>
          {/* TODO: wrap the handle click function */}
          <NewButton type="primary" onClick={() => undefined} disabled={validationError !== null}>
            <FormattedMessage {...messages.save} />
          </NewButton>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationModal;
